import { API_URL, APP_VERSION, COMMIT_HASH, LOG_LEVEL } from './constants'

const FLUSH_INTERVAL_MS = 5000
const FLUSH_THRESHOLD = 20
const MAX_BATCH_SIZE = 100
const MAX_BUFFER_SIZE = 500

const IS_LOCALHOST =
	typeof window !== 'undefined' &&
	(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
const FORCE_CW = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('logcw')
const SHOULD_SEND = !IS_LOCALHOST || FORCE_CW

const LEVELS = { debug: 0, info: 1, warn: 2, error: 3 }
const MIN_LEVEL = LEVELS[LOG_LEVEL] ?? LEVELS.info

/**
 * Console ring attached to error reports — the seconds before the throw, at full resolution.
 *
 * Off localhost `LOG_LEVEL` is `warn`, so `log()` drops info entries before they reach the buffer and they exist nowhere, client or server. This ring is written above that gate, making it the only record of what the app was doing when it died.
 *
 * ⚠️ In-memory only, never persisted: it carries prompt text and user content.
 */
const RING_SIZE = 30
const RING_MAX_BYTES = 8000
const RING_ENTRY_DATA_CHARS = 300

let _store = null
let _selectSessionId = null
let _getRollbarContext = null

function parseArgs(args) {
	let prefix = null
	const messageParts = []
	const dataParts = []
	let hitNonPrimitive = false

	for (const arg of args) {
		if (!hitNonPrimitive && typeof arg === 'string') {
			if (!prefix && messageParts.length === 0 && /^\[.+\]$/.test(arg.trim())) {
				prefix = arg.trim().slice(1, -1)
			} else {
				messageParts.push(arg)
			}
		} else if (!hitNonPrimitive && (typeof arg === 'number' || typeof arg === 'boolean')) {
			messageParts.push(String(arg))
		} else if (!hitNonPrimitive && (arg === null || arg === undefined)) {
			messageParts.push(String(arg))
		} else {
			hitNonPrimitive = true
			if (arg instanceof Error) {
				dataParts.push({ error: arg.message, stack: arg.stack })
			} else if (typeof arg === 'object' && arg !== null) {
				dataParts.push(arg)
			} else {
				dataParts.push(arg)
			}
		}
	}

	const message = messageParts.join(' ')
	let data = undefined
	if (dataParts.length === 1) data = dataParts[0]
	else if (dataParts.length > 1) data = dataParts

	return { prefix, message, data }
}

/**
 * Flatten a log entry's `data` to a bounded string.
 *
 * Called only from `_ringSnapshot`, i.e. once per reported error — never on the logging path. `data` is whatever a caller passed to `console.log`, up to whole store slices, and stringifying one costs milliseconds; doing that per call would tax every `console.*` in the app to serialize entries that are discarded unread. The ring therefore holds the raw reference and pays this cost only when an error actually reports.
 *
 * ⚠️ Consequence: an object mutated between the log and the throw is captured as it looks at THROW time.
 *
 * Circular refs come back as the throw's own message rather than killing the report.
 */
function summarizeForRing(data) {
	try {
		const s = typeof data === 'string' ? data : JSON.stringify(data)
		return s == null ? String(data) : s.slice(0, RING_ENTRY_DATA_CHARS)
	} catch {
		return '[unserializable]'
	}
}

class FeLogger {
	constructor() {
		this.apiKey = null
		this.buffer = []
		this.flushTimer = null
		this.endpoint = `${API_URL}/fe-logs`
		this.lastByPrefix = new Map()
		this.ring = []
	}

	setApiKey(key) {
		if (!key) return
		this.apiKey = key
		this._startTimer()
		window.addEventListener('visibilitychange', this._onVisibilityChange)
		window.addEventListener('beforeunload', this._onBeforeUnload)
	}

	log(level, prefix, message, data) {
		// Above both gates: the entries worth having are exactly the ones `MIN_LEVEL` is about to drop.
		this._ringPush(level, prefix, message, data)

		if (!SHOULD_SEND) return
		if ((LEVELS[level] ?? 0) < MIN_LEVEL) return

		const key = prefix ?? ''

		if (data === undefined) {
			const last = this.lastByPrefix.get(key)
			if (last && last.message === message) {
				last.count++
				last.lastTimestamp = Date.now()
				return
			}
			this._flushRepeat(key)
			this.lastByPrefix.set(key, {
				message,
				level,
				count: 1,
				firstTimestamp: Date.now(),
				lastTimestamp: Date.now(),
			})
		} else {
			this._flushRepeat(key)
			this.lastByPrefix.delete(key)
		}

		const entry = { timestamp: Date.now(), level, message, route: window.location.pathname }
		if (prefix) entry.prefix = prefix
		if (data !== undefined) entry.data = data
		this.buffer.push(entry)

		if (this.buffer.length > MAX_BUFFER_SIZE) {
			this.buffer.shift()
		}

		if (this.buffer.length >= FLUSH_THRESHOLD) {
			this.flush()
		}
	}

	_flushRepeat(key) {
		const last = this.lastByPrefix.get(key)
		if (!last || last.count <= 1) return
		const durationMs = last.lastTimestamp - last.firstTimestamp
		const durationStr =
			durationMs < 60000 ? `${Math.round(durationMs / 1000)}s` : `${Math.round(durationMs / 60000)}m`
		const entry = {
			timestamp: last.lastTimestamp,
			level: last.level,
			message: `${last.message} [repeated ${last.count} times over ${durationStr}]`,
			route: window.location.pathname,
		}
		if (key) entry.prefix = key
		this.buffer.push(entry)
		this.lastByPrefix.delete(key)
	}

	/**
	 * One ring entry, capped by count here and by total bytes at read time in `_ringSnapshot`.
	 *
	 * Runs on EVERY `console.*` call in the app, so it does no serializing work — `data` is stored by reference and flattened only if an error reports (see `summarizeForRing`).
	 */
	_ringPush(level, prefix, message, data) {
		const entry = { t: Date.now(), level, message: String(message ?? '').slice(0, RING_ENTRY_DATA_CHARS) }
		if (prefix) entry.prefix = prefix
		if (data !== undefined) entry.data = data
		this.ring.push(entry)
		if (this.ring.length > RING_SIZE) this.ring.shift()
	}

	/**
	 * Newest-last, trimmed from the FRONT until it fits the byte cap — the entries nearest the throw are the ones worth keeping.
	 */
	_ringSnapshot() {
		const out = this.ring.map(e => (e.data === undefined ? e : { ...e, data: summarizeForRing(e.data) }))
		while (out.length && JSON.stringify(out).length > RING_MAX_BYTES) out.shift()
		return out
	}

	reportError(error, context) {
		if (!this.apiKey || (IS_LOCALHOST && !FORCE_CW)) return

		const message = error?.message || String(error)
		const stack = error?.stack || new Error(message).stack

		let extraContext = {}
		try {
			if (_getRollbarContext) extraContext = _getRollbarContext()
			if (_store) {
				const user = _store.getState().auth?.user
				if (user?.email) extraContext.userId = user.email
			}
		} catch {
			/* noop */
		}

		this._send({
			action: 'error',
			message,
			stack,
			name: error?.name || 'Error',
			codeVersion: COMMIT_HASH,
			level: 'error',
			context: {
				...extraContext,
				// Absolute, unlike `route`: Rollbar's `request.url` drives its URL filtering and occurrence links, and a bare pathname degrades both.
				href: window.location.href,
				route: window.location.pathname,
				browser: navigator.userAgent,
				appVersion: APP_VERSION,
				commitHash: COMMIT_HASH,
				// After the spread: `getRollbarContext` reports a null session before one exists, and that null is what orc would replace with an id the client never learns.
				session_id: this._getSessionId(),
				console_ring: this._ringSnapshot(),
				...context,
			},
		})
	}

	flush() {
		for (const key of this.lastByPrefix.keys()) {
			this._flushRepeat(key)
		}
		if (!this.apiKey || this.buffer.length === 0) return

		const sessionId = this._getSessionId()

		while (this.buffer.length > 0) {
			const batch = this.buffer.splice(0, MAX_BATCH_SIZE)
			this._send({
				action: 'log',
				sessionId,
				logs: batch,
			})
		}
	}

	_send(payload) {
		fetch(this.endpoint, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-Api-Key': this.apiKey,
			},
			body: JSON.stringify(payload),
			keepalive: true,
		}).catch(() => {})
	}

	/**
	 * The real session id once one exists, a stable per-tab id before that.
	 *
	 * ⚠️ The fallback is what makes pre-session errors findable at all. Without an id here orc mints a `uuidv4()` per request, so a batch and the error report that followed it land under different keys and neither can be queried by anything the client knows.
	 */
	_getSessionId() {
		try {
			if (_store && _selectSessionId) {
				const id = _selectSessionId(_store.getState())
				if (id) return id
			}
		} catch {
			/* noop */
		}
		if (!this.fallbackSessionId)
			this.fallbackSessionId = `tab-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`
		return this.fallbackSessionId
	}

	_startTimer() {
		if (this.flushTimer) return
		this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS)
	}

	_onVisibilityChange = () => {
		if (document.visibilityState === 'hidden') {
			this.flush()
		}
	}

	_onBeforeUnload = () => {
		this.flush()
	}
}

const feLogger = new FeLogger()

// Global console intercept
const _originals = {
	log: console.log,
	info: console.info,
	warn: console.warn,
	error: console.error,
	debug: console.debug,
}

const LEVEL_MAP = { log: 'info', info: 'info', warn: 'warn', error: 'error', debug: 'debug' }

Object.entries(_originals).forEach(([method, original]) => {
	console[method] = (...args) => {
		original.apply(console, args)
		const level = LEVEL_MAP[method]
		const { prefix, message, data } = parseArgs(args)
		feLogger.log(level, prefix, message, data)
	}
})

console.scope = prefix => ({
	debug: (...args) => console.debug(`[${prefix}]`, ...args),
	log: (...args) => console.log(`[${prefix}]`, ...args),
	info: (...args) => console.log(`[${prefix}]`, ...args),
	warn: (...args) => console.warn(`[${prefix}]`, ...args),
	error: (...args) => console.error(`[${prefix}]`, ...args),
})

export function initFeLogger(store, selectSessionId, getRollbarContext) {
	_store = store
	_selectSessionId = selectSessionId
	_getRollbarContext = getRollbarContext
}

export default feLogger
